<i18n>
[
    "product__size--label",
    "product__noSizeSelected--label",
    "product__skuSoldOutColor--message",
    "product__allSoldOut--message",
    "product__skuSoldOut--message",
]
</i18n>

<template>
    <div>
        <div class="u-spacer--1pt5 o-text--caption">
            <span>
                {{ $t("product__size--label") }}
                <span
                    v-if="selectedSku"
                    class="c-color-size-selector__size-text"
                >
                    {{ sizeDisplayName }}
                </span>
                <span v-else class="c-color-size-selector__no-size-selected-text">
                    {{ $t("product__noSizeSelected--label") }}
                </span>
            </span>
        </div>
        <SkuSelector
            :skuSelectorId="id"
            :skuSelectorName="id"
            :sizeGroups="selectedChoice.sizeGroups"
            :selectedColorCode="colorCode"
            :initialSizeType="sizeType"
            :initialSize="size"
            :initialSku="selectedSku"
            :purchasableSkus="purchasableSkus"
            :emitClickEvent="true"
            @update-sku="skuUpdated"
            @click="sizeClick"
        />
        <p
            v-if="noInventory && (!hasError || addToBoxError)"
            class="c-color-size-selector__sold-out-text o-text--caption u-spacer--2"
        >
            {{ $t("product__allSoldOut--message") }}
        </p>
        <BaseSupportingTextError
            v-if="hasError && !addToBoxError"
            class="c-color-size-selector__error u-spacer--3"
            :errorText="soldOutMessage"
        />
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import SkuSelector from '~modules/core/components/ui/SkuSelector.vue';
import BaseSupportingTextError from '~coreModules/core/components/ui/form-elements/BaseSupportingTextError.vue';

const props = defineProps({
    id: {
        type: String,
        required: true,
    },
    selectedChoice: {
        type: Object,
        required: true,
    },
    colorCode: {
        type: String,
        required: true,
    },
    sizeCodeAvailibilityByChoiceId: {
        type: Object,
        required: true,
    },
    selectedSku: {
        type: String,
        default: '',
    },
    selectedSkuSizeCode: {
        type: Object,
        default: null,
    },
    addToBoxError: {
        type: String,
        default: '',
    },
    setSkuCode: {
        type: Function,
        required: true,
    },
    setSkuSizeCode: {
        type: Function,
        required: true,
    },
    setErrorText: {
        type: Function,
        default: () => ({}),
    },
    updateItemInBoxStatus: {
        type: Function,
        default: () => ({}),
    },
    purchasableSkus: {
        type: Array,
        default: () => [],
    },
});

const { t } = useI18n();

const sizeType = ref('');
const size = ref('');
const sizeDisplayName = ref('');
const soldOutMessage = ref('');
const hasError = ref(false);

const noInventory = computed(() => {
    const sizeGroups = props.selectedChoice?.sizeGroups ?? [];
    const hasInventory = sizeGroups.find(({ includedSkus }) => includedSkus.find(sku => sku.availableInventory > 0));
    return !hasInventory;
});

const isSelectedSkuSizeCodeAvailableInSelectedChoice = computed(() => (
    props.sizeCodeAvailibilityByChoiceId[props.selectedChoice.choiceId]
        ?.includes(props.selectedSkuSizeCode?.code)
));

function skuUpdated(sku) {
    props.setSkuCode?.(sku?.skuId || '');
    props.setSkuSizeCode?.(sku?.size || props.selectedSkuSizeCode);
    props.updateItemInBoxStatus?.(false);
    props.setErrorText?.('');
}

function sizeClick(event) {
    if (event.disabled && noInventory.value) {
        soldOutMessage.value = t('product__allSoldOut--message');
        hasError.value = true;
    } else if (event.disabled && !noInventory.value) {
        props.setSkuCode?.('');
        props.setSkuSizeCode?.(null);
        size.value = '';
        soldOutMessage.value = t('product__skuSoldOut--message', [event.label]);
        hasError.value = true;
    } else {
        sizeDisplayName.value = event.label;
        hasError.value = false;
        soldOutMessage.value = '';
    }
}

function setCommonSelectorProperties({ skuId, sizeDisplayName: newSizeDisplayName, hasError: newHasError }) {
    props.setSkuCode?.(skuId);
    sizeDisplayName.value = newSizeDisplayName;
    hasError.value = newHasError;
}

function setSkuSoldOut() {
    setCommonSelectorProperties({
        skuId: '',
        sizeDisplayName: '',
        hasError: true,
    });
    size.value = '';
    sizeType.value = props.selectedChoice.sizeGroups?.[0]?.groupCode;
    soldOutMessage.value = t(
        'product__skuSoldOutColor--message',
        [props.selectedSkuSizeCode?.displayName,
            props.selectedChoice?.color?.displayName],
    );
}

function handleSkuPreselection(sku) {
    let preselectedSku = sku;

    if (!sku) {
        if (props.purchasableSkus?.length) {
            preselectedSku = props.purchasableSkus?.[0];
        } else {
            return;
        }
    }

    let skuObject;

    /* eslint-disable-next-line arrow-body-style */
    props.selectedChoice.sizeGroups.find((sizeGroup) => {
        return sizeGroup.includedSkus.find((includedSku) => {
            if (includedSku.skuId === preselectedSku) {
                if (includedSku.availableInventory === 0 && props.selectedSkuSizeCode?.code) {
                    setSkuSoldOut();
                } else if (includedSku.availableInventory > 0 || props.purchasableSkus.includes(preselectedSku)) {
                    size.value = includedSku?.size?.code ?? '';
                    sizeDisplayName.value = includedSku?.size?.displayName ?? '';
                    sizeType.value = sizeGroup?.groupCode ?? '';
                    skuObject = includedSku;
                    return true;
                }
            }

            return false;
        });
    });

    skuUpdated(skuObject);
}

function findAvailableSkuForChoiceId() {
    let foundSkuData = {};

    props.selectedChoice.sizeGroups.find(sizeGroup => sizeGroup.includedSkus.find((includedSku) => {
        if (includedSku?.size?.code === props.selectedSkuSizeCode?.code) {
            foundSkuData = {
                ...includedSku,
                sizeType: sizeGroup?.groupCode || '',
            };
            return true;
        }
        return false;
    }));

    return foundSkuData;
}

watch(() => props.colorCode, (newColor, oldColor) => {
    if (newColor === oldColor) return;

    if (props.selectedSkuSizeCode) {
        const skuData = findAvailableSkuForChoiceId();
        if (isSelectedSkuSizeCodeAvailableInSelectedChoice.value) {
            setCommonSelectorProperties({
                skuId: skuData?.skuId,
                sizeDisplayName: skuData?.size?.displayName,
                hasError: false,
            });
            size.value = skuData?.size?.code;
            sizeType.value = skuData?.sizeType;
        } else if (!skuData?.skuId) {
            setCommonSelectorProperties({
                skuId: '',
                sizeDisplayName: '',
                hasError: false,
            });
            size.value = '';
            sizeType.value = props.selectedChoice.sizeGroups?.[0]?.groupCode;
        } else {
            setSkuSoldOut();
        }
    } else {
        hasError.value = false;
    }
});

onMounted(() => {
    handleSkuPreselection(props.selectedSku);
});

watch(() => props.selectedSku, (newSku, oldSku) => {
    if (newSku && newSku !== oldSku) {
        hasError.value = false;
        handleSkuPreselection(newSku);
    }
});

</script>

<style lang="scss">
    .c-color-size-selector {
        &__size-text,
        &__no-size-selected-text,
        &__sold-out-text {
            color: $nu-gray;
        }

        &__error {
            margin-top: 0;
        }
    }
</style>
